import React from 'react';

import type { TFunction } from 'i18next';
import isString from 'lodash/isString';

/**
 * truncate the string at the specific length, using the ... as a suffix.
 * @param {string} label string that needs to be truncated
 * @param {number} len the length want to keep of the label
 * @returns {string} return the label or truncatedString
 */

const SUFFIX = '...';
export const truncateLabel = (label: string, len: number): string => {
  if (label.length > len) {
    return `${label.slice(0, len)}${SUFFIX}`;
  }

  return label;
};

/** @deprecated Leverage TypeScript to verify if your value is falsy instead. */
export function isEmpty(value: unknown): boolean {
  let isValueEmpty = false;

  if (
    value === undefined ||
    value === null ||
    (isString(value) && value === '') ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' &&
      value !== null &&
      Object.keys(value).length === 0)
  ) {
    isValueEmpty = true;
  }

  return isValueEmpty;
}

/** @deprecated Leverage TypeScript to verify if your value is falsy instead. */
export function isNotEmpty(value: unknown): boolean {
  return !isEmpty(value);
}

type Scaled = { num: string; suffix: string };
export const MISSING_VALUE_PLACEHOLDER = '---';
export const FILES_SIZE_LABELS = [
  'fileAttachment:fileSize.bytes',
  'fileAttachment:fileSize.kb',
  'fileAttachment:fileSize.mb',
  'fileAttachment:fileSize.gb',
  'fileAttachment:fileSize.tb',
  'fileAttachment:fileSize.pb',
];

/**
 * Scales a number to the specified base and with the specified precision
 * @param {number|string} input - the number to scale
 * @param {number} base - the base to divide by
 * @param {number} maxDecimals - maximum digits after the decimal (precision)
 * @return {{num: number, suffix: string}} - a two-property object (Scaled) with the scaled number and unit suffix
 */
export const scaleValue = (
  input: number | string,
  base: number,
  maxDecimals: number,
  t: TFunction,
  sizeLabels: string[] | undefined = FILES_SIZE_LABELS,
): Scaled => {
  const value = typeof input !== 'number' ? parseFloat(input) : input;
  let index = 0;
  let num: string;
  const sizes = sizeLabels.map((label) => t(label));

  if (Number.isNaN(value)) {
    num = MISSING_VALUE_PLACEHOLDER;
  } else {
    index = Math.floor(Math.log(value) / Math.log(base));
    index = Math.max(0, Math.min(index, sizes.length - 1)); // lock index range between 0 and sizes.length-1
    num = (value / base ** index)
      .toFixed(index > 0 ? maxDecimals : 0)
      .toString();
  }
  return { num, suffix: sizes[index] };
};

/**
 * Returns a string with first char of each word as capital.
 * @param {string} string - string as group of words.
 * @return string - a string with first char of each word as capital.
 */
export const convertToTitleCase = (string = ''): string => {
  let str = string;
  if (str.includes('_')) {
    str = str.replace('_', ' ');
  }
  str = str.toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());
  return str;
};

/**
 * Returns true if value is a JSON else, it returns false.
 * @param {string} strValue - string.
 * @return boolean - returns true if value is a JSON else, it returns false.
 */
export const isJson = (strValue: string): boolean => {
  try {
    JSON.parse(strValue);
  } catch {
    return false;
  }
  return true;
};

/**
 * Returns JSON in pretty format if value is a JSON else, it returns the actual string value.
 * @param {string} strValue - string.
 * @return React.ReactNode - returns JSON in pretty format if value is a JSON else, it returns the actual string value.
 */
export const getPrettyJson = (strValue: string): React.ReactNode => {
  try {
    const parsedValue = JSON.parse(strValue);
    return <pre>{JSON.stringify(parsedValue, null, 2)}</pre>;
  } catch {
    return strValue;
  }
};

/**
 * Scrolls the current window to the top.
 */
export const scrollToTop = (): void => {
  window.scrollTo({ left: 0, top: 0 });
};
